import React from 'react';
import _ from 'lodash';

import * as MassActionFeedbackChannel from 'app/entities/MassActionFeedbackChannel';
import { Profile } from '@zapier/shared-entities';
import TaskReplayCompleteMessageContainer from 'app/viewport/components/TaskReplayCompleteMessageContainer';
import staticNotifications, {
  STATIC_NOTIFICATIONS_STORAGE_KEY,
} from 'app/viewport/staticNotifications';

export const NEW_STATUS = 'new';
export const DELIVERED_STATUS = 'delivered';
export const READ_STATUS = 'read';

export const TASK_ACTION_DETAILS = 'Task Action Details';

export const getChannelNotificationTitle = channel => {
  if (MassActionFeedbackChannel.selectors.hasGlobalFailures(channel)) {
    return 'couldn’t perform action';
  }

  const played = MassActionFeedbackChannel.selectors.playedCount(channel);
  const total = MassActionFeedbackChannel.selectors.totalCount(channel);

  return MassActionFeedbackChannel.selectors.isRunning(channel)
    ? `${played} of ${total} attempted`
    : TASK_ACTION_DETAILS;
};

const getBadgeKind = channel => {
  if (MassActionFeedbackChannel.selectors.hasGlobalFailures(channel)) {
    return 'error';
  }
  if (MassActionFeedbackChannel.selectors.isRunning(channel)) {
    return 'in-progress';
  }
  return 'done';
};

const ICON_TYPES = {
  error: 'bell',
  'in-progress': 'zap-arrow',
  done: 'bell',
};

const makeFeedbackChannelNotification = channel => {
  const title = getChannelNotificationTitle(channel);
  const badgeKind = getBadgeKind(channel);
  const allStatusCounts = MassActionFeedbackChannel.selectors.allStatusCounts(
    channel
  );
  const unreplayableErrors = MassActionFeedbackChannel.selectors.unreplayableErrors(
    channel
  );
  const hasProgress = !MassActionFeedbackChannel.selectors.hasGlobalFailures(
    channel
  );
  const globalFailures = MassActionFeedbackChannel.selectors.globalFailures(
    channel
  );
  const globalFailureType = MassActionFeedbackChannel.selectors.globalFailureType(
    channel
  );
  const channelId = MassActionFeedbackChannel.selectors.id(channel);
  const action = MassActionFeedbackChannel.selectors.action(channel);

  return {
    type: 'taskReplayNotification',
    id: MassActionFeedbackChannel.selectors.id(channel),
    title,
    message: () =>
      badgeKind === 'in-progress' ? null : (
        <TaskReplayCompleteMessageContainer
          action={action}
          channelId={channelId}
          globalFailures={globalFailures}
          globalFailureType={globalFailureType}
          statusCounts={allStatusCounts}
          unreplayableErrors={unreplayableErrors}
        />
      ),
    badgeKind,
    iconType: ICON_TYPES[badgeKind],
    isImportant: true,
    progressMax: hasProgress
      ? MassActionFeedbackChannel.selectors.totalCount(channel)
      : 1,
    progressValue: hasProgress
      ? MassActionFeedbackChannel.selectors.playedCount(channel)
      : 1,
    hasButton: false,
    isDismissable: badgeKind === 'done' || badgeKind === 'error',
    isMassActionsDisabled: MassActionFeedbackChannel.selectors.isMassActionsDisabled(
      channel
    ),
  };
};

const isStaticNotificationId = notificationId => {
  const id = String(notificationId);
  return id.endsWith('_1');
};

const getStaticNotificationsDismissed = () => {
  const dismissed = window.sessionStorage.getItem(
    STATIC_NOTIFICATIONS_STORAGE_KEY
  );
  return dismissed ? dismissed.split(',') : [];
};

const setStaticNotificationsDismissed = notifications => {
  window.sessionStorage.setItem(
    STATIC_NOTIFICATIONS_STORAGE_KEY,
    notifications.join(',')
  );
};

export const makeNotifications = (savedNotifications, state) => {
  const profile = Profile.selectors.all.currentProfile(state);
  const channelNotifications = MassActionFeedbackChannel.selectors.all
    .entities(state)
    .map(makeFeedbackChannelNotification);
  const getStaticNotificationId = n => `${n.type}_1`;
  const staticNotificationsDismissed = getStaticNotificationsDismissed();
  const filteredStaticNotifications = staticNotifications.filter(
    n =>
      n.isApplicable(profile, state) &&
      !staticNotificationsDismissed.find(
        sn => sn === getStaticNotificationId(n)
      )
  );
  const allNotifications = channelNotifications.concat(
    filteredStaticNotifications
  );

  return allNotifications.map(n => {
    const id = n.id || getStaticNotificationId(n);
    const notificationStatus = savedNotifications[id]
      ? savedNotifications[id].notificationStatus
      : NEW_STATUS;
    return { id, notificationStatus, ..._.omit(n, 'isApplicable') };
  });
};

export const maybeDismissStaticNotification = notificationId => {
  if (isStaticNotificationId(notificationId)) {
    const staticNotificationsDismissed = getStaticNotificationsDismissed();
    setStaticNotificationsDismissed(
      staticNotificationsDismissed.concat([notificationId])
    );
  }
};

export const sortNotifications = notifications => {
  return notifications.sort((a, b) => {
    if (a.isImportant && !b.isImportant) {
      return -1;
    }
    if (b.isImportant && !a.isImportant) {
      return 1;
    }
    return (b.createdAt || 0) - (a.createdAt || 0);
  });
};

const isUnread = notification => notification.notificationStatus !== 'read';

export const hasUnreadNotifications = notifications =>
  _.some(notifications, isUnread);

export const hasUnreadImportantNotifications = notifications =>
  _.some(notifications, n => n.isImportant && isUnread(n));

export const isNew = status => status === NEW_STATUS;
export const isDelivered = status =>
  status === DELIVERED_STATUS || status === READ_STATUS;
export const isRead = status => status === READ_STATUS;
